import { MouseEvent, useMemo } from 'react';
import cn from 'classnames';

import { buildTagRows } from '../utils';

import { Tag } from '../../types/tagTypes';

import styles from './TagsTable.module.scss';

type TagsTableProps = {
    tags: Tag[];
    onTagClick: (id: Tag['id']) => void;
    onDeleteTagClick: (e: MouseEvent<HTMLButtonElement>, id: Tag['id']) => void;
    onCopyTagClick: (e: MouseEvent<HTMLButtonElement>, id: Tag['id']) => void;
    isSelectionMode?: boolean;
    selectedTagIds?: ReadonlySet<Tag['id']>;
    onToggleTagSelection?: (id: Tag['id']) => void;
};

export function TagsTable(props: TagsTableProps) {
    const {
        tags,
        onTagClick,
        onDeleteTagClick,
        onCopyTagClick,
        isSelectionMode = false,
        selectedTagIds,
        onToggleTagSelection,
    } = props;

    const rows = useMemo(() => buildTagRows(tags), [tags]);

    return (
        <table className={styles.tagsTable}>
            <thead>
                <tr>
                    {isSelectionMode && <th className={styles.checkboxCell} />}
                    <th>Name</th>
                    <th>ID</th>
                    <th className={cn(styles.alignRight)}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(({ tag, depth, key }) => {
                    const isSelected = selectedTagIds?.has(tag.id) ?? false;
                    const onRowClick = () => {
                        if (isSelectionMode) {
                            onToggleTagSelection?.(tag.id);
                            return;
                        }
                        onTagClick(tag.id);
                    };
                    return (
                        <tr
                            key={key}
                            className={cn(isSelected && styles.selectedRow)}
                            onClick={onRowClick}
                        >
                            {isSelectionMode && (
                                <td
                                    className={styles.checkboxCell}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        aria-label={`select ${tag.stringId}`}
                                        checked={isSelected}
                                        onChange={() =>
                                            onToggleTagSelection?.(tag.id)
                                        }
                                    />
                                </td>
                            )}
                            <td>
                                <div
                                    className={cn(
                                        styles.nameCell,
                                        depth > 0 && styles.indentation,
                                    )}
                                >
                                    <div className={styles.icon}>
                                        {tag.iconMedia && (
                                            <img
                                                src={tag.iconMedia.url.replace(
                                                    '{0}',
                                                    '@2x',
                                                )}
                                                alt={`${tag.stringId} icon`}
                                            />
                                        )}
                                    </div>
                                    <span>{tag.name.en}</span>
                                </div>
                            </td>
                            <td className={styles.idCell}>{tag.id}</td>
                            <td
                                className={cn(
                                    styles.alignRight,
                                    styles.buttons,
                                )}
                            >
                                {!isSelectionMode && (
                                    <>
                                        <button
                                            className="primary small"
                                            onClick={() => onTagClick(tag.id)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="small"
                                            onClick={(e) =>
                                                onCopyTagClick(e, tag.id)
                                            }
                                        >
                                            Copy to...
                                        </button>
                                        <button
                                            className="danger small"
                                            onClick={(e) =>
                                                onDeleteTagClick(e, tag.id)
                                            }
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
